import { Box, Flex, Popover, Text, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconStar, IconStarFilled } from '@tabler/icons-react';
import { useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { DocumentVersion } from 'src/suitability-report/hooks/useDocumentVersions';
import { VersionPopoverContent } from './versioning';

type VersionTileProps = {
  version: string;
  date: string;
  variant: 'primary' | 'past';
  versions: DocumentVersion[];
  selectedVersionId?: string;
  onSaveVersion: () => void;
  onRemoveVersion: (versionId: string) => void;
  onOverwriteVersion: (versionId: string) => void;
  onSelectVersion: (versionId: string) => void;
  onMakePrimary: (versionId: string) => void;
  onDownloadPdf: (fileId: number) => void;
};

export default function VersionTile({
  version,
  date,
  variant,
  versions,
  selectedVersionId,
  onSaveVersion,
  onRemoveVersion,
  onOverwriteVersion,
  onSelectVersion,
  onMakePrimary,
  onDownloadPdf,
}: VersionTileProps) {
  const isPrimary = variant === 'primary';
  const [opened, setOpened] = useState(false);

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom" offset={10}>
      <Popover.Target>
        <UnstyledButton
          onClick={() => setOpened(o => !o)}
          aria-expanded={opened}
          aria-haspopup="dialog"
          aria-label={`Version ${version}, ${date}. Click to view all versions`}
        >
          <Flex
            align="center"
            gap="xxs"
            h={28}
            px="xs"
            bg={isPrimary ? 'brand.1' : undefined}
            style={{
              borderRadius: 'var(--mantine-radius-xl)',
              border: isPrimary ? undefined : '1px solid var(--mantine-color-brand-4)',
            }}
          >
            {isPrimary ? (
              <IconStarFilled size={16} color="var(--mantine-color-warning-6)" />
            ) : (
              <IconStar size={16} color="var(--mantine-color-gray-4)" />
            )}
            <Text size="sm" c="black">
              <Text span fw={FONT_WEIGHTS.bold}>
                {version}
              </Text>
              : {date}
            </Text>
            <IconChevronDown size={16} color="black" />
          </Flex>
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown
        p={0}
        style={{
          border: '1px solid var(--mantine-color-gray-2)',
          borderRadius: 12,
          overflow: 'hidden',
        }}
      >
        <Box p="sm" w={{ base: 300, md: 468 }}>
          <VersionPopoverContent
            versions={versions}
            selectedVersionId={selectedVersionId}
            onSaveVersion={onSaveVersion}
            onRemoveVersion={onRemoveVersion}
            onOverwriteVersion={onOverwriteVersion}
            onSelectVersion={onSelectVersion}
            onMakePrimary={onMakePrimary}
            onDownloadPdf={onDownloadPdf}
          />
        </Box>
      </Popover.Dropdown>
    </Popover>
  );
}
